import { h, defineComponent, Ref, SetupContext, computed, ComputedRef } from 'vue';
// @ts-ignore
import { BButton } from 'buefy/dist/esm/button';
import { Icon } from '@/ui/components/icon/Icon';
import { Tooltip } from '@/ui/components/Tooltip';
import { t } from '../i18n/i18n';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import Spinner from '@/ui/components/Spinner.vue';

export interface ButtonProps {
  type?: string;
  btnClass?: string;
  iconLeft?: string;
  iconRight?: string;
  iconPack?: string;
  loading?: boolean; //standard buefy loading. It hides entire text on the button and shows only spinner
  loadingWithText?: boolean; //our custom loading. It shows spinner next to the button contents and disables the button.
  tag?: string;
  disabled?: boolean;
  outlined?: boolean;
  size?: string;
}

export interface SquareButtonProps {
  text: string;
  type?: string;
  disabled?: boolean;
  icon?: string;
  iconPack?: string;
}

export const Button = defineComponent<ButtonProps>({
  name: 'Button',
  props: ['type', 'btnClass', 'iconLeft', 'iconRight', 'iconPack', 'loading', 'loadingWithText', 'tag', 'disabled', 'outlined', 'size'],
  setup(props, context: SetupContext) {
    const className = props.btnClass ? props.btnClass : 'has-text-weight-semibold';
    return function render() {
      // @ts-ignore
      return h(
        BButton,
        {
          class: [className, 'is-disabled'],
          props: {
            ...props,
            type: props.type || 'is-primary',
            iconPack: props.iconPack || 'far',
          },
          attrs: {
            ...context.attrs,
            // The `disabled` is being used as a prop because on newer versions
            // of buefy the button component receives a prop `disabled`.
            // when we update buefy it will be an easier transition
            disabled: props.disabled ?? props.loadingWithText ?? false,
          },
          on: context.listeners,
        },
        [context.slots.default?.(), props.loadingWithText ? h(Spinner, { class: 'ml-2' }) : null],
      );
    };
  },
});

export const SquareButton = defineComponent<SquareButtonProps>({
  name: 'SquareButton',
  props: ['text', 'type', 'disabled', 'icon', 'iconPack'],
  setup(props, ctx) {
    return function render() {
      return h(Button, {
        class: 'button-square',
        props: {
          type: props.type ?? 'is-primary',
        },
        attrs: {
          disabled: props.disabled ?? false,
        },
        on: {
          click: () => ctx.emit('click'),
        },
      }, [
        h('div', { class: 'button-square__container' }, [
          h(Icon, {
            props: {
              icon: props.icon ?? 'plus',
              iconPack: props.iconPack ?? 'far',
              size: '2x',
            },
          }),
        ]),
        h('span', { class: 'button-square__text' }, [props.text]),
      ]);
    };
  },
});

export function button(text: string, click: Function, attrs: { [key: string]: unknown } = {}, props: ButtonProps = {}) {
  return h(Button, {
    on: { click },
    attrs,
    props,
  }, text);
}

export function loadingButton(
  text: string,
  click: Function,
  isLoading: boolean,
  attrs: { [key: string]: unknown } = {},
  isDisabled?: boolean,
) {
  return h(
    Button,
    {
      class: 'is-loading-button',
      on: { click },
      attrs: { loading: isLoading, disabled: isDisabled || isLoading, ...attrs },
    },
    text,
  );
}

export function createSquareButton(
  text: string,
  click: Function,
  type = 'is-primary',
  disabled = false,
  icon = 'plus',
  iconPack = 'far',
) {
  return h(SquareButton, {
    props: {
      text,
      type,
      disabled,
      icon,
      iconPack,
    },
    on: {
      click,
    },
  });
}

// TODO: Consider to use BulkDeleteButton.vue, the template component version of this function.
export function bulkDeleteButton<T = unknown>(
  { resources, disabled }: { resources: Ref<T[]>; disabled?: ComputedRef<boolean> },
) {
  const isDisabled = computed(() => disabled
    ? disabled.value || resources.value.length < 1
    : resources.value.length < 1);

  return h(Tooltip, { props: { label: t('common.delete-selected'), position: 'is-right' } }, [
    h(Button, {
      class: `button--bulk-delete ${resources.value.length > 0 ? 'button--bulk-delete--active' : ''}`,
      props: { type: 'is-light', disabled: isDisabled.value },
      on: { click: () => { EventBus.$emit(Events.bulkDeleteClick); } },
    }, [
      h(Icon, {
        props: {
          icon: 'trash-alt',
          size: 'sm',
        },
      }),
    ]),
  ]);
}

export function markAsReadButton<T = unknown>(
  { resources, disabled }: { resources: Ref<T[]>; disabled?: Ref<boolean> },
) {
  const isDisabled = computed(() => disabled
    ? disabled.value || resources.value.length < 1
    : resources.value.length < 1);

  return h(Button, {
    class: `button--bulk-delete ${resources.value.length > 0 ? 'button--bulk-delete--active' : ''}`,
    props: { type: 'is-light', disabled: isDisabled.value },
    on: { click: () => { EventBus.$emit(Events.bulkUpdateClick); } },
  }, t('notifications.mark-as-read'));
}
